import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ConfirmationService } from '../../../../shared/services/confirmation.service';
import { switchMap, map, catchError } from 'rxjs/operators';
import { AccountsPayableService } from '../../../../services/accounts-payable.service';
import { AccountsPayable, AccountsPayableUpdate } from '../../../../models/AccountsPayable';
import { AccountsPayableAttachment } from '../../../../models/AccountsPayableAttachment';
import { AuthService } from '../../../../services/auth.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { CostCentersService } from '../../../../services/cost-centers.service';
import { CashflowService } from '../../../../services/cashflow.service';
import { canPayAccountsPayable } from '../../../../utils/accounts-payable.utils';
import {
  ACCOUNTS_PAYABLE_ATTACHMENT_CATEGORIES,
  AccountsPayableAttachmentCategory,
  DEFAULT_ACCOUNTS_PAYABLE_ATTACHMENT_CATEGORY
} from '../../../../shared/constants/accounts-payable-attachment-categories';

export interface PendingAccountsPayableAttachment {
  file: File;
  category: AccountsPayableAttachmentCategory;
}

@Component({
  selector: 'app-accounts-payable-view',
  templateUrl: './accounts-payable-view.component.html',
  styleUrls: ['./accounts-payable-view.component.css']
})
export class AccountsPayableViewComponent implements OnInit {
  accountsPayable: AccountsPayable | null = null;
  accountsPayableId?: number;
  loading = false;
  error: string | null = null;

  showPaymentModal = false;
  paymentDate: string = '';
  paymentAmount: number = 0;
  paymentMethod: string = '';
  paymentReference: string = '';
  paymentMethodOptions = ['Boleto', 'Transferência', 'PIX', 'Cartão', 'Cheque', 'Dinheiro'];

  /** Saldo disponível do centro de custo (quando a conta tem costCenterId). */
  availableBalance: number | null = null;
  loadingAvailableBalance = false;
  /** Erro exibido no modal de pagamento (ex.: saldo insuficiente 400). */
  paymentModalError: string | null = null;

  /** Abre o modal de pagamento após carregar, se veio da lista com ?action=pay. */
  private pendingPayFromQuery = false;

  // Attachments (vêm do GET da conta e do POST /attachments)
  attachments: AccountsPayableAttachment[] = [];
  uploadingFile = false;
  downloadingAttachmentId: number | null = null;
  readonly attachmentCategoryOptions = ACCOUNTS_PAYABLE_ATTACHMENT_CATEGORIES;
  selectedAttachmentCategory: AccountsPayableAttachmentCategory =
    DEFAULT_ACCOUNTS_PAYABLE_ATTACHMENT_CATEGORY;
  paymentModalAttachmentCategory: AccountsPayableAttachmentCategory =
    DEFAULT_ACCOUNTS_PAYABLE_ATTACHMENT_CATEGORY;

  /** Arquivos selecionados no modal de pagamento (enviados ao confirmar). */
  pendingReceiptFiles: PendingAccountsPayableAttachment[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountsPayableService: AccountsPayableService,
    private authService: AuthService,
    private errorService: ErrorService,
    private costCentersService: CostCentersService,
    private cashflowService: CashflowService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.accountsPayableId = +params['id'];
      this.loadAccountsPayable();
    });

    // Verificar se há ação específica na query string
    this.route.queryParams.subscribe(queryParams => {
      if (queryParams['action'] === 'pay') {
        this.pendingPayFromQuery = true;
        this.tryOpenPaymentFromQuery();
      }
    });
  }

  loadAccountsPayable(): void {
    if (!this.accountsPayableId) {
      this.error = 'ID da conta a pagar não fornecido';
      return;
    }

    this.loading = true;
    this.error = null;

    this.accountsPayableService.getAccountsPayableById(this.accountsPayableId).subscribe({
      next: (accountsPayable) => {
        this.accountsPayable = accountsPayable;
        this.syncAttachmentsFromAccount();
        this.loading = false;
        this.tryOpenPaymentFromQuery();
      },
      error: (error) => {
        console.error('Erro ao carregar conta a pagar:', error);
        this.error = 'Erro ao carregar dados da conta a pagar';
        this.loading = false;
      }
    });
  }

  formatDate(date: string | Date): string {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  formatCurrency(value: number): string {
    if (value === null || value === undefined) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'pendente':
        return 'status-pending';
      case 'aprovado':
        return 'status-approved';
      case 'pago':
      case 'paid':
        return 'status-paid';
      case 'cancelado':
        return 'status-cancelled';
      case 'vencido':
        return 'status-overdue';
      case 'parcialmente pago':
        return 'status-partial';
      default:
        return 'status-default';
    }
  }

  isOverdue(): boolean {
    if (!this.accountsPayable) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dueDate = new Date(this.accountsPayable.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    
    return dueDate < today && this.accountsPayable.status.toLowerCase() === 'pendente';
  }

  canPay(): boolean {
    return canPayAccountsPayable(this.accountsPayable?.status);
  }

  private tryOpenPaymentFromQuery(): void {
    if (!this.pendingPayFromQuery || !this.accountsPayable) return;
    this.pendingPayFromQuery = false;
    if (this.canPay()) {
      this.openPaymentModal();
    }
  }

  editAccountsPayable(): void {
    if (this.accountsPayableId) {
      this.router.navigate(['/financial/accounts-payable', this.accountsPayableId, 'edit']);
    }
  }

  payAccountsPayable(): void {
    if (!this.canPay()) return;
    this.openPaymentModal();
  }

  printAccountsPayable(): void {
    if (!this.accountsPayable) return;
    
    // Implementar lógica de impressão
    
    // Por enquanto, vamos usar window.print()
    window.print();
  }

  deleteAccountsPayable(): void {
    if (!this.accountsPayable || !this.accountsPayableId) return;

    const doc = this.accountsPayable.documentNumber || String(this.accountsPayableId);
    this.confirmationService
      .confirmDelete(doc, 'a conta a pagar')
      .pipe(take(1))
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.accountsPayableService.deleteAccountsPayable(this.accountsPayableId!).subscribe({
          next: () => this.router.navigate(['/financial/accounts-payable']),
          error: (error) => {
            console.error('Erro ao excluir conta a pagar:', error);
            this.error = 'Erro ao excluir conta a pagar';
          }
        });
      });
  }

  goBack(): void {
    this.router.navigate(['/financial/accounts-payable']);
  }

  // Attachments Methods
  private getUploadedBy(): string {
    return this.authService.getUserNameFromToken()?.trim() || 'system';
  }

  private syncAttachmentsFromAccount(): void {
    this.attachments = [...(this.accountsPayable?.attachments ?? [])];
  }

  private reloadAccountAttachments(): void {
    if (!this.accountsPayableId) return;
    this.accountsPayableService.getAccountsPayableById(this.accountsPayableId).subscribe({
      next: (ap) => {
        this.accountsPayable = ap;
        this.syncAttachmentsFromAccount();
      },
      error: (err) => console.error('Erro ao recarregar anexos', err)
    });
  }

  getAttachmentDisplayName(file: AccountsPayableAttachment): string {
    const raw = file?.originalFileName || file?.fileName || '';
    return raw.split('/').pop() || raw || 'Arquivo';
  }

  getAttachmentUrl(file: AccountsPayableAttachment): string | null {
    const url = (file?.filePath || file?.blobUrl || '').trim();
    return url || null;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (file) {
      this.uploadAttachmentImmediately(file, this.selectedAttachmentCategory);
    }
    if (input) input.value = '';
  }

  onPaymentModalFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input?.files;
    if (!files?.length) return;
    for (let i = 0; i < files.length; i++) {
      this.pendingReceiptFiles.push({
        file: files[i],
        category: this.paymentModalAttachmentCategory
      });
    }
    if (input) input.value = '';
  }

  removePendingReceipt(index: number): void {
    this.pendingReceiptFiles.splice(index, 1);
  }

  private uploadAttachmentImmediately(file: File, category: AccountsPayableAttachmentCategory): void {
    if (!this.accountsPayableId) return;
    this.uploadingFile = true;
    this.accountsPayableService
      .uploadAttachment(this.accountsPayableId, file, {
        category,
        uploadedBy: this.getUploadedBy()
      })
      .subscribe({
        next: (att) => {
          this.uploadingFile = false;
          this.attachments = [...this.attachments, att];
          this.errorService.showInfoMessage('Documento anexado com sucesso!');
        },
        error: () => {
          this.uploadingFile = false;
        }
      });
  }

  private uploadPendingReceipts(): Observable<AccountsPayableAttachment[]> {
    if (!this.pendingReceiptFiles.length || !this.accountsPayableId) {
      return of([]);
    }
    const uploads = this.pendingReceiptFiles.map(item =>
      this.accountsPayableService
        .uploadAttachment(this.accountsPayableId!, item.file, {
          category: item.category,
          uploadedBy: this.getUploadedBy()
        })
        .pipe(catchError(() => of(null)))
    );
    return forkJoin(uploads).pipe(
      map(results => results.filter((r): r is AccountsPayableAttachment => r != null))
    );
  }

  canDownloadAttachment(file: AccountsPayableAttachment): boolean {
    return !!(file?.id || this.getAttachmentUrl(file));
  }

  isDownloadingAttachment(file: AccountsPayableAttachment): boolean {
    return file?.id != null && this.downloadingAttachmentId === file.id;
  }

  openAttachment(file: AccountsPayableAttachment): void {
    const url = this.getAttachmentUrl(file);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (this.accountsPayableId && file.id) {
      this.accountsPayableService
        .getAttachmentDownloadUrl(this.accountsPayableId, file.id)
        .subscribe({
          next: (downloadUrl) => window.open(downloadUrl, '_blank', 'noopener,noreferrer'),
          error: () => this.errorService.showErrorMessage('URL do comprovante não disponível.')
        });
      return;
    }
    this.errorService.showErrorMessage('URL do comprovante não disponível.');
  }

  downloadAttachment(file: AccountsPayableAttachment): void {
    const fileName = this.getAttachmentDisplayName(file);
    if (!this.accountsPayableId) return;

    if (file.id) {
      this.downloadingAttachmentId = file.id;
      this.accountsPayableService.downloadAttachment(this.accountsPayableId, file.id).subscribe({
        next: (blob) => {
          this.saveBlobAsFile(blob, fileName);
          this.downloadingAttachmentId = null;
        },
        error: () => {
          this.accountsPayableService
            .getAttachmentDownloadUrl(this.accountsPayableId!, file.id!)
            .subscribe({
              next: (url) => {
                this.triggerFileDownload(url, fileName);
                this.downloadingAttachmentId = null;
              },
              error: () => {
                this.downloadingAttachmentId = null;
                this.downloadFromDirectUrl(file, fileName);
              }
            });
        }
      });
      return;
    }

    this.downloadFromDirectUrl(file, fileName);
  }

  private downloadFromDirectUrl(file: AccountsPayableAttachment, fileName: string): void {
    const url = this.getAttachmentUrl(file);
    if (!url) {
      this.errorService.showErrorMessage('Não foi possível baixar o comprovante.');
      return;
    }
    this.triggerFileDownload(url, fileName);
  }

  private triggerFileDownload(url: string, fileName: string): void {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  private saveBlobAsFile(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  }

  /** Saldo ainda a pagar (remaining da API ou amountToPay − amountPaid). */
  getRemainingPayableAmount(ap: AccountsPayable | null = this.accountsPayable): number {
    if (!ap) return 0;
    const toPay = Number(ap.amountToPay) || 0;
    const paid = Number(ap.amountPaid) || 0;
    const remaining = ap.remainingAmount;
    if (remaining != null && remaining !== undefined && !isNaN(Number(remaining))) {
      return Math.max(0, Math.round(Number(remaining) * 100) / 100);
    }
    return Math.max(0, Math.round((toPay - paid) * 100) / 100);
  }

  formatMoneyBr(value: number | null | undefined): string {
    if (value == null || value === undefined || isNaN(Number(value))) return '';
    return Number(value).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  onPaymentAmountInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = (input.value || '').replace(/\D/g, '').slice(0, 15);
    const value = digits === '' ? 0 : parseInt(digits, 10) / 100;
    this.paymentAmount = value;
    input.value = this.formatMoneyBr(value);
  }

  onPaymentAmountFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = this.formatMoneyBr(this.paymentAmount);
  }

  openPaymentModal(): void {
    if (!this.canPay()) return;
    this.showPaymentModal = true;
    this.paymentModalError = null;
    this.availableBalance = null;
    this.pendingReceiptFiles = [];
    this.paymentModalAttachmentCategory = DEFAULT_ACCOUNTS_PAYABLE_ATTACHMENT_CATEGORY;
    this.syncAttachmentsFromAccount();
    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const dd = String(today.getDate()).padStart(2, '0');
    this.paymentDate = `${yyyy}-${mm}-${dd}`;
    if (this.accountsPayable) {
      this.paymentAmount = this.getRemainingPayableAmount(this.accountsPayable);
      this.paymentMethod = (this.accountsPayable.paymentMethod || '').trim() || this.paymentMethodOptions[0] || '';
      const costCenterId = this.accountsPayable.costCenterId ?? undefined;
      if (costCenterId) {
        this.loadingAvailableBalance = true;
        this.costCentersService.getAvailableBalance(costCenterId).subscribe({
          next: (res) => {
            this.availableBalance = res.availableBalance;
            this.loadingAvailableBalance = false;
          },
          error: () => {
            this.loadingAvailableBalance = false;
          }
        });
      }
    } else {
      this.paymentAmount = 0;
    }
  }

  closePaymentModal(): void {
    this.showPaymentModal = false;
    this.paymentModalError = null;
    this.availableBalance = null;
    this.pendingReceiptFiles = [];
  }

  /** Indica se o valor do pagamento é maior que o saldo disponível do centro de custo. */
  isPaymentAboveAvailableBalance(): boolean {
    if (this.availableBalance == null) return false;
    const paid = Number(this.paymentAmount) || 0;
    return paid > this.availableBalance;
  }

  submitPayment(): void {
    if (!this.accountsPayable || !this.canPay()) return;
    const ap = this.accountsPayable;
    const paidAmount = Number(this.paymentAmount) || 0;
    if (paidAmount <= 0 || !this.paymentDate) return;

    const previousPaid = ap.amountPaid || 0;
    const baseRemaining = this.getRemainingPayableAmount(ap) - paidAmount;
    const status = baseRemaining <= 0 ? 'Pago' : 'Parcialmente Pago';

    // Helper function to convert to number or undefined
    const toNumberOrUndefined = (value: any): number | undefined => {
      if (value === null || value === undefined || value === '' || value === 'null') {
        return undefined;
      }
      const num = Number(value);
      return isNaN(num) ? undefined : num;
    };

    const updated: AccountsPayableUpdate = {
      id: ap.id,
      documentNumber: ap.documentNumber,
      documentType: ap.documentType,
      issueDate: ap.issueDate,
      dueDate: ap.dueDate,
      paymentDate: this.paymentDate,
      originalAmount: Number(ap.originalAmount),
      discountAmount: toNumberOrUndefined(ap.discountAmount),
      interestAmount: toNumberOrUndefined(ap.interestAmount),
      fineAmount: toNumberOrUndefined(ap.fineAmount),
      amountToPay: Number(ap.amountToPay),
      amountPaid: previousPaid + paidAmount,
      status,
      category: ap.category,
      paymentMethod: this.paymentMethod || ap.paymentMethod,
      paymentReference: this.paymentReference || ap.paymentReference,
      supplierId: toNumberOrUndefined(ap.supplierId),
      costCenterId: toNumberOrUndefined(ap.costCenterId),
      approvedByStaffId: toNumberOrUndefined(ap.approvedByStaffId),
      paidByStaffId: toNumberOrUndefined(ap.paidByStaffId),
      description: ap.description,
      invoiceNumber: ap.invoiceNumber,
      purchaseOrderNumber: ap.purchaseOrderNumber,
      installmentNumber: toNumberOrUndefined(ap.installmentNumber),
      totalInstallments: toNumberOrUndefined(ap.totalInstallments),
      bankSlipNumber: ap.bankSlipNumber,
      checkNumber: ap.checkNumber
    };

    this.loading = true;
    this.paymentModalError = null;
    this.uploadPendingReceipts()
      .pipe(switchMap(uploaded => {
        if (uploaded.length) {
          this.attachments = [...this.attachments, ...uploaded];
        }
        return this.accountsPayableService.updateAccountsPayable(updated);
      }))
      .subscribe({
        next: (res) => {
          this.accountsPayable = res;
          this.syncAttachmentsFromAccount();
          this.loading = false;
          this.pendingReceiptFiles = [];
          this.reloadAccountAttachments();
          this.closePaymentModal();
          this.cashflowService.requestRefresh();
          this.errorService.showInfoMessage('Pagamento registrado com sucesso!');
        },
        error: (err) => {
          this.loading = false;
          const status = err?.status;
          const body = err?.error;
          if (status === 400 && (body?.message || typeof body === 'string')) {
            this.paymentModalError = typeof body === 'string' ? body : (body.message || body.title || 'Saldo disponível do centro de custo é insuficiente.');
          } else {
            this.paymentModalError = body?.message || err?.message || 'Erro ao efetuar pagamento.';
          }
          console.error(err);
        }
      });
  }
}
